<script lang="ts">
    import { createEventDispatcher, onMount, tick } from 'svelte/internal'
    import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
    import type { UserCreateDto } from '../../modules/usersApi/users/dto/create-user.dto'
    import type { UserUpdateDto } from '../../modules/usersApi/users/dto/user-update.dto'
    import { User } from '../../modules/usersApi/users/user'
    import { UsersRepository } from '../../modules/usersApi/users/users.repository'
    import { UsersService } from '../../modules/usersApi/users/users.service'
    import ObUsersItem from './ob-users-item.svelte'
    import ObStep from './ob-step.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { Store } from '../../modules/infra/store.service'
    import { UserPermissions } from '../../modules/usersApi/users/dto/user-permissions.dto'
    const dispatch = createEventDispatcher()
    let min = 0
    let total = 0
    let users = []
    let show = false
    let loaded = false
    const rep = new UsersRepository()
    const service = new UsersService(rep)
    const store = new Store()

    // a lista começa no que já existe: não dá para ter menos usuários do que os já cadastrados
    $: totalOptions = Array.from({ length: 100 }, (_, index) => ({ value: index + min, label: String(index + min) }))

    function totalChange() {
        while (users.length != total) {
            if (users.length > total) {
                users.pop()
                continue
            }
            if (users.length < total) {
                let s: any = new User()
                s.superUser = false
                s.superUserLoaded = false
                users.push(s)
            }
        }
        users = users
    }

    function onBack() {
        if (users.length > 0) {
            if (!confirm('As alterações dessa tela serão perdidas ao voltar. Deseja mesmo voltar?')) return false
        }
        dispatch('back')
    }

    let saving = false
    async function onNext() {
        if (saving) return
        saving = true
        if (await saveEntity()) {
            dispatch('next')
        }
        await tick()
        saving = false
    }

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            let result = await window.api.users.companies.getCompanyUsers()
            min = result.length
            total = result.length
            users = result
            await loadSupers()
            loaded = true
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    // o toggle "Super" precisa refletir a realidade de quem já existe, senão a tela mente: quem nunca
    // teve permissões gravadas é super por padrão histórico (a API hidrata o objeto vazio com super).
    async function loadSupers() {
        await Promise.all(
            users.map(async (user) => {
                user.superUser = false
                user.superUserLoaded = false
                if (!user.userId) return
                try {
                    const perms = await service.getPermissions(user.userId)
                    if (!perms) return
                    user.permissions = perms
                    user.superUser = perms.super === true
                    user.superUserLoaded = user.superUser
                } catch (error) {
                    // falha ao ler permissão não trava o onboarding: o toggle fica desligado e só grava
                    // se a pessoa mexer nele
                }
            }),
        )
        users = users
    }

    async function createEntity(entity) {
        try {
            show = true
            const dto: UserCreateDto = {
                userName: entity.userName,
                phone: entity.phone,
                email: entity.email,
            }
            const created = await service.create(dto)
            if (created?.userId) entity.userId = created.userId
            entity.isNew = true
            return true
        } catch (error) {
            const connectedUserId = await tryToConnectExistingUser(error)
            if (connectedUserId) {
                // usuário já existia e acabou de ser vinculado: o vínculo é novo, então a permissão
                // precisa ser gravada como qualquer outro criado aqui
                entity.userId = connectedUserId
                entity.isNew = true
                return true
            }
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    async function updateEntity(entity) {
        try {
            show = true
            const dto: UserUpdateDto = {
                userName: entity.userName,
                phone: entity.phone,
                email: entity.email,
            }
            entity = await service.update(entity.userId, dto)
            return true
        } catch (error) {
            if (await tryToConnectExistingUser(error)) return true
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    // a permissão "super" não vive no usuário, e sim no vínculo com a empresa — daí a chamada separada.
    // Só grava quando muda (ou quando o vínculo é novo), para não sobrescrever permissões já ajustadas
    // na tela de permissões de quem já estava na empresa.
    async function saveSuper(entity) {
        if (!entity.userId) return true
        // o próprio usuário logado não tem o toggle na tela (em regra é o proprietário), e a API também
        // recusaria a alteração das próprias permissões
        if (entity.userId === store.token.userId) return true
        if (!entity.isNew && !!entity.superUser === !!entity.superUserLoaded) return true
        try {
            show = true
            const perms: UserPermissions = entity.permissions || new UserPermissions()
            perms.super = !!entity.superUser
            entity.permissions = await service.updatePermissions(entity.userId, perms)
            entity.superUserLoaded = !!entity.superUser
            entity.isNew = false
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    // devolve o userId vinculado (ou null): quem chama precisa dele para gravar a permissão de super
    async function tryToConnectExistingUser(error) {
        console.log('try', error)
        if (error) {
            if (error.userId) {
                await connectUser(error.userId)
                return error.userId
            }
            if (error?.message && error.message.includes('userId:')) {
                const userId = error.message.split(':')[1]
                await connectUser(userId)
                return userId
            }
        }
        return null
    }

    async function connectUser(userId: string) {
        const store = new Store()
        if (!store.token.companyId) return null
        await window.api.users.companies.assignUser(store.token.companyId, userId)
    }

    function validateAll() {
        for (let entity of users) {
            if (!validateFields(entity)) return false
        }
        return true
    }

    async function saveEntity() {
        users = users.filter((el) => !!el)
        if (!validateAll()) return false
        for (let entity of users) {
            if (entity.userId) {
                if (!(await updateEntity(entity))) return false
            } else {
                if (!(await createEntity(entity))) return false
            }
            if (!(await saveSuper(entity))) return false
        }
        return true
    }

    function validateFields(entity) {
        if (!entity.userName) {
            window.error('Informe o nome do usuário')
            return false
        }
        if (!entity.phone) {
            window.error('Informe o telefone do usuário')
            return false
        }
        if (entity.email && !entity.email.includes('@')) {
            window.error('Informe um e-mail válido.')
            return false
        }
        return true
    }
</script>

<ObStep
    title="Usuários / Atendentes"
    label="Informe os nomes dos usuários que poderão ser informados ao lançar um registro na agenda ou no ponto de vendas."
>
    <svelte:fragment slot="acts">
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={onBack} />
        <Zbutton color="primary" icon={faAngleRight} label="Avançar" loading={saving} disabled={saving} on:click={onNext} />
    </svelte:fragment>

    <ZContainer gapRow="8px" gapCol="8px">
        <Zinput size={4} sizeM={12} label="Quantidade de usuários" type="select" options={totalOptions} bind:value={total} on:change={totalChange} />
    </ZContainer>

    {#each users as user}
        <ObUsersItem bind:user showSuper={user?.userId !== store.token.userId} />
    {/each}

    <div class="mt-3 text-muted">
        <b>Quantidade de usuários:</b><br />
        Representa a quantidade de pessoas que desempenham um papel em sua empresa.<br />
        Ex: Se você possui 2 atendentes + 1 secretária + 1 gestor (você) então são 4 usuários<br />
        Ex: Se você possui 1 atendente + 1 gestor (você) então são 2 usuários<br />
        Ex: Se uma pessoa desempanha mais de um papel (como atentendente + gestor) basta 1 usuário para esta pessoa
    </div>
    {#if users.length > 0}
        <div class="mt-3 text-muted">
            <b>Celular e e-mail:</b><br />
            São dados necessários para que sua equipe consiga entrar no Zandu e acompanhar a própria agenda, por exemplo.<br />
        </div>
    {/if}
</ObStep>

<LoadSpinner {show} />
